import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Award, MapPin } from 'lucide-react';
function AnimatedStat({
  end,
  suffix = '',
  prefix = '',
  label,
  sublabel,
  icon: Icon,
  delay = 0
}: {
  end: number;
  suffix?: string;
  prefix?: string;
  label: string;
  sublabel: string;
  icon: React.ElementType;
  delay?: number;
}) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  useEffect(() => {
    if (!isInView) return;
    let startTime: number;
    const duration = 2; // seconds
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = (timestamp - startTime) / (duration * 1000);
      if (progress < 1) {
        setCount(Math.floor(end * progress));
        requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };
    requestAnimationFrame(animate);
  }, [end, isInView]);
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 24
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.6,
    delay
  }} className="text-center relative z-10 px-4" data-id="element-178" data-mp="rse9cvt9seof">
      <div className="w-14 h-14 rounded-full bg-brand-gold/15 border border-brand-gold/30 flex items-center justify-center mx-auto mb-5" data-id="element-179" data-mp="dtelhqdl03tb">
        <Icon className="w-6 h-6 text-brand-gold" data-id="element-180" data-mp="16ghhaaqvi0fv" />
      </div>
      <div className="text-5xl md:text-6xl lg:text-7xl font-serif font-bold text-brand-gold mb-3 leading-none" data-id="element-181" data-mp="16h0ytqcm2mtd">
        {prefix}
        {count}
        {suffix}
      </div>
      <div className="text-white font-semibold text-base md:text-lg uppercase tracking-wider mb-1" data-id="element-182" data-mp="wpczz4dq63hv">
        {label}
      </div>
      <div className="text-white/60 text-sm font-light" data-id="element-183" data-mp="1u74u9afnbg9a">{sublabel}</div>
    </motion.div>;
}
export function ImpactStats() {
  return <section className="relative py-24 border-y border-white/10 overflow-hidden" data-id="element-184" data-mp="28blxwf246kwt">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{
      backgroundImage: 'url(https://images.unsplash.com/photo-1521737711867-e3b97375f902?ixlib=rb-4.0.3&auto=format&fit=crop&w=2400&q=90)'
    }} data-id="element-185" data-mp="2d9dnv3j77wyd"></div>
      <div className="absolute inset-0 bg-brand-navy/90 mix-blend-multiply" data-id="element-186" data-mp="1pkb86184ke6i"></div>
      <div className="absolute inset-0 bg-brand-navy/80" data-id="element-187" data-mp="1ujxlsbrv7wfu"></div>

      {/* Decorative gold glows */}
      <div className="absolute -top-24 -right-24 w-72 h-72 bg-brand-gold/10 rounded-full blur-3xl" data-id="element-188" data-mp="1gk61jtar9jte"></div>
      <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-brand-gold/10 rounded-full blur-3xl" data-id="element-189" data-mp="1klhrjzxjk724"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-190" data-mp="3axnqnkqt6oqj">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center max-w-2xl mx-auto mb-16" data-id="element-191" data-mp="3f0an5yyfcxu2">
          <h2 className="text-brand-gold font-semibold tracking-widest uppercase text-sm mb-4" data-id="element-192" data-mp="1d4367tvlsgld">
            By The Numbers
          </h2>
          <h3 className="text-3xl md:text-4xl font-serif text-white leading-tight" data-id="element-193" data-mp="1mzmzs9mp336y">
            A trusted partner, coast to coast.
          </h3>
        </motion.div>

        {/* Stats */}
        <div className="relative flex flex-col sm:flex-row items-center justify-center gap-12 sm:gap-0 max-w-3xl mx-auto" data-id="element-194" data-mp="1w1mhz59g4dbh">
          <div className="flex-1" data-id="element-195" data-mp="1r32uv1j48vhj">
            <AnimatedStat end={14} suffix="+" label="Years in Business" sublabel="Serving carriers, TPAs & employers" icon={Award} data-id="element-196" data-mp="1qzx29cba3bh2" />
          </div>

          {/* Divider */}
          <div className="hidden sm:block w-px h-32 bg-gradient-to-b from-transparent via-brand-gold/40 to-transparent" data-id="element-197" data-mp="3v6zx303m83ye"></div>

          <div className="flex-1" data-id="element-198" data-mp="32fj6p33oxxs">
            <AnimatedStat end={50} suffix=" States" label="Nationwide Reach" sublabel="Coverage in all 50 states" icon={MapPin} delay={0.15} data-id="element-199" data-mp="83bwft9cdwfv" />
          </div>
        </div>
      </div>
    </section>;
}